// В.2.a: Лив Лайф — утилита воскрешения юнита из стопки сброса.
//
// Используется будущими картами Лив Лайф (Опытный хирург, Главная Лаборатория,
// Доктор Франк, Матрица Клонирования, Феникс, Главврач Ариана) — они появятся
// в шагах В.2.b и далее. В каркасе В.2.a эту функцию никто не вызывает,
// проверяем только в тестах.
//
// Поведение (диздок 6.6, диздок Лив Лайф):
//   - Карта возвращается на поле тем же объектом из сброса (ссылка та же),
//     чтобы накопленные модификаторы (Моды Синтетика, permanent-баффы,
//     keyword'ы, custom-флаги) сохранились.
//   - current.hp = ceil(base.hp / 2). base.hp не меняется.
//   - current.armor = max_armor (полное восстановление брони, как у
//     свежевыставленной карты).
//   - current.atk остаётся как было до смерти (Моды и баффы атаки сохраняются).
//   - НЕ проходит через Initiation Cooldown — задаём initiation = 0 на инстансе,
//     чтобы isSleeping(currentTurn) всегда вернул false.
//   - Триггеры «появился юнит» / «выставлен» НЕ срабатывают на воскрешение —
//     это не Приказ повторно. Карты, желающие реагировать на воскрешение,
//     должны сделать это в своей логике через явное событие resurrected.
//   - Карта извлекается из p.discard ПОСЛЕ успешного помещения на поле.
//   - Возвращает true, если воскрешение состоялось; false — если на стороне
//     нет свободного слота (или slot занят / не валиден / карта не в сбросе).

// В.2.g: post-resurrect-хуки (Лиза +2/1/0). fn(state, card, ownerIdx).
const _postResurrectHooks = [];
export function registerPostResurrectHook(fn) {
  if (typeof fn !== 'function') return;
  _postResurrectHooks.push(fn);
}
export function _resetPostResurrectHooksForTests() {
  _postResurrectHooks.length = 0;
}

// Воскресить deadCard на стороне side в slot.
//   state    — game state.
//   deadCard — Card-инстанс, лежащий в стопке сброса игрока side.
//   side     — 'player' | 'enemy' (на чьей стороне поставить).
//   slot     — { line: 0|1, idx: 0..3 } или null (выбрать случайный свободный).
//   opts     — { hpMode: 'half_hp' | 'full_hp', sourceOwnerIdx } для C.1.
//              hpMode='half_hp' (по умолчанию) — current_hp = ceil(base/2).
//              hpMode='full_hp' (Лиза-trigger/active) — current_hp = base_hp.
//              sourceOwnerIdx — индекс игрока чьим становится воскрешённый.
//                Если задан и НЕ равен индексу стороны, где сейчас сброс —
//                карта мигрирует из чужого сброса в собственный field
//                (угон у Лизы-активки).
// Возвращает true | false.
export function resurrectUnit(state, deadCard, side, slot = null, opts = {}) {
  if (!state || !deadCard) return false;
  if (deadCard.type !== 'unit') return false;

  const sideIdx = sideToIdx(state, side, deadCard);
  if (sideIdx == null) return false;
  const player = state.players[sideIdx];
  if (!player) return false;

  // Выбор слота.
  let line, idx;
  if (slot && Number.isInteger(slot.line) && Number.isInteger(slot.idx)) {
    line = slot.line; idx = slot.idx;
    if (line < 0 || line > 1 || idx < 0 || idx > 3) return false;
    if (player.field[line][idx] !== null) return false;
  } else {
    const free = player.freeSlots();
    if (free.length === 0) return false;
    const pick = free[state.rng.int(free.length)];
    line = pick.line; idx = pick.slot;
  }

  // Карта должна быть в стопке сброса игрока.
  const discardIdx = player.discard.indexOf(deadCard);
  if (discardIdx === -1) return false;

  // Изменения статов.
  const baseHp = deadCard.base?.hp ?? 0;
  const halfUp = Math.max(1, Math.ceil(baseHp / 2));
  const hpMode = opts?.hpMode === 'full_hp' ? 'full_hp' : 'half_hp';
  if (!deadCard.current) deadCard.current = { atk: 0, armor: 0, hp: 0 };
  deadCard.current.hp = hpMode === 'full_hp' ? baseHp : halfUp;
  // Полное восстановление брони — как у свежевыставленной карты.
  // Используем maxArmorWithMods чтобы учесть permanent-бонусы (Моды и т.п.).
  const maxArmor = (typeof deadCard.maxArmorWithMods === 'number')
    ? deadCard.maxArmorWithMods
    : (deadCard.base?.armor ?? 0);
  deadCard.current.armor = maxArmor;
  // current.atk не меняем — сохраняются Моды и баффы атаки.

  // Сброс «мёртвого» состояния.
  deadCard.alive = true;
  deadCard.line = null;
  deadCard.slot = null;
  deadCard.lastLine = null;
  deadCard.lastSlot = null;

  // Игнорировать Initiation Cooldown — мгновенно активен в Ф3 этого хода.
  deadCard.initiation = 0;
  deadCard.placedTurn = state.currentTurn;
  deadCard.revealed = true;
  deadCard.activated = false;
  deadCard.heroAttackedThisTurn = false;
  deadCard.counterAttackedThisPhase = false;

  // Снять источники Молчания (на воскрешённом — чистый лист по способностям).
  if (deadCard.silenceSources) deadCard.silenceSources.clear();
  if (deadCard.current) deadCard.current.silenced = false;

  // Поставить на поле.
  try {
    player.placeCard(deadCard, line, idx);
  } catch {
    return false;
  }

  // Извлечь из сброса (после успешного размещения).
  player.discard.splice(discardIdx, 1);

  state.log?.add(
    `🩺 ${deadCard.name_ru} [${player.name}] воскрешён ` +
    `(HP ${deadCard.current.hp}/${baseHp}, броня ${deadCard.current.armor}/${maxArmor})`
  );

  // В.2.g: post-resurrect-хуки. Вызываются ПОСЛЕ полного помещения карты
  // на поле (Лиза баффает +2/1/0; будущие хуки могут добавлять триггеры).
  for (const hook of _postResurrectHooks) {
    try { hook(state, deadCard, sideIdx); }
    catch (e) { console.warn('[Корпы] post-resurrect-hook упал:', e); }
  }

  return true;
}

// Преобразовать side ('player' | 'enemy' | 0 | 1) в индекс state.players.
//   - Если 'player'/'enemy' — определяем относительно владельца deadCard (его
//     deadCard.ownerId уже зафиксирован в момент смерти).
//   - Если число 0/1 — берём как есть.
function sideToIdx(state, side, deadCard) {
  if (side === 0 || side === 1) return side;
  if (side === 'player') {
    return deadCard.ownerId ?? 0;
  }
  if (side === 'enemy') {
    if (deadCard.ownerId == null) return null;
    return 1 - deadCard.ownerId;
  }
  return null;
}
